import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { trackPageView } from './services/analytics';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Login } from './components/Auth/Login';
import { SignUp } from './components/Auth/SignUp';
import { Dashboard } from './components/Dashboard/Dashboard';
import { Resume } from './components/Resume/Resume';
import { ProtectedRoute } from './components/ProtectedRoute';
import { HomePage } from './components/HomePage';
import { JobSearch } from './components/JobSearch';
import { ResumeTuner } from './pages/ResumeTuner/ResumeTuner';
import { InterviewPractice } from './pages/InterviewPractice/InterviewPractice';
import { CareerInterview } from './pages/CareerInterview/CareerInterview';
import { CareerTimeline } from './pages/CareerTimeline/CareerTimeline';
import { StatusPage } from './pages/Status/StatusPage';
import { LinkedInPostsPage } from './pages/LinkedInPosts/LinkedInPostsPage';
import { WeeklyCheckInPage } from './pages/WeeklyCheckIn/WeeklyCheckInPage';
import { ResumeHistory } from './pages/ResumeHistory/ResumeHistory';
import { SavedResumes } from './pages/SavedResumes/SavedResumes';
import { AdminPage } from './pages/Admin/AdminPage';
import { SettingsPage } from './pages/Settings/SettingsPage';
import { CareerVisionPage } from './pages/CareerVision/CareerVisionPage';
import ExperienceGraphPage from './pages/ExperienceGraph/ExperienceGraphPage';
import { AppLayout } from './components/Layout/AppLayout';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { TermsPage } from './pages/Legal/TermsPage';
import { PrivacyPage } from './pages/Legal/PrivacyPage';

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RouteTracker />
      <BreadcrumbProvider>
        <LanguageProvider>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Dashboard />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/resume"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Resume />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/resume-history"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <ResumeHistory />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/saved-resumes"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <SavedResumes />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/job-search"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <JobSearch />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/resume-tuner"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <ResumeTuner />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/interview-practice"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <InterviewPractice />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/career-interview"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <CareerInterview />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/career-timeline"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <CareerTimeline />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/linkedin-posts"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <LinkedInPostsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weekly-checkin"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <WeeklyCheckInPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/career-vision"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <CareerVisionPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <SettingsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/experience-graph"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <ExperienceGraphPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/status" element={<AppLayout><StatusPage /></AppLayout>} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <AdminPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </LanguageProvider>
      </BreadcrumbProvider>
    </Router>
  );
}

export default App;
